import {
  format,
  parseISO,
  isValid,
  differenceInDays,
  addDays,
  isPast,
  parse,
  getHours,
  getMinutes,
  startOfDay,
  compareAsc,
} from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import i18n from '../../../app/i18n';

export const formatTime = ({ hour, minute = 0, timezone }) => {
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  const minuteStr = minute.toString().padStart(2, '0');
  const time = `${hour12}:${minuteStr}`;
  const meridiem = hour < 12 ? 'AM' : 'PM';
  const label = `${time} ${meridiem} ${timezone}`;
  return label;
};

// Converts minutes from midnight to { hour, minute }
export const minutesToTime = minutes => {
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  return { hour, minute };
};

// Converts { hour, minute } to minutes from midnight
export const timeToMinutes = (hour, minute = 0) => hour * 60 + minute;

// Hourly time options (used by campaigns) - values are hours 0-23
export const timeOptions = timezone => {
  return [...Array(24).keys()].map(hour => {
    return { value: hour, label: formatTime({ hour, minute: 0, timezone }) };
  });
};

// 15-minute interval time options (used by broadcasts) - values are minutes from midnight
export const timeOptions15Min = timezone => {
  const options = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const value = timeToMinutes(hour, minute);
      options.push({
        value,
        label: formatTime({ hour, minute, timezone }),
      });
    }
  }
  return options;
};

const DEFAULT_FALLBACK = i18n.t('posts:unscheduledPost');
const DATE_FORMAT = 'MMMM d, yyyy';

export const formatDate = (date, dateFormat = DATE_FORMAT) =>
  format(date, dateFormat);

export const parseDate = (
  date,
  dateFallback = DEFAULT_FALLBACK,
  dateFormat,
) => {
  const dateISO = parseISO(date);
  return isValid(dateISO) ? formatDate(dateISO, dateFormat) : dateFallback;
};

export const getDifferenceInDays = (reminderDate, publishDate) => {
  const reminderISO = parseISO(reminderDate);
  const publishISO = parseISO(publishDate);

  if (!isValid(reminderISO) || !isValid(publishISO)) return 0;

  return differenceInDays(reminderISO, publishISO);
};

export const getLiveReminderDate = (reminderDate, publishDate) => {
  if (!reminderDate) return null;

  const diff = getDifferenceInDays(reminderDate, publishDate);

  return formatDate(addDays(new Date(), diff), 'yyyy-MM-dd');
};

export const dateHasPassed = (publishDate, sendTime, timezone) => {
  const inputTimeStr = `${publishDate} ${sendTime}`;
  const inputTime = parse(inputTimeStr, 'yyyy-MM-dd hh:mm a', new Date(), {
    timeZone: timezone,
  });

  return isPast(inputTime);
};

export const getZonedDate = timezone => {
  return startOfDay(utcToZonedTime(new Date(), timezone));
};

export const isTodayInZone = (timezone, date) => {
  const dateISO = parseISO(date);
  const parsedDate = isValid(dateISO) ? dateISO : date;

  return compareAsc(getZonedDate(timezone), parsedDate) === 0;
};

// Returns the next available hour (used by campaigns)
export const nextAvailableHour = timezone => {
  const zonedTime = utcToZonedTime(new Date(), timezone);
  const hour = getHours(zonedTime);
  const minute = getMinutes(zonedTime);

  if (hour >= 23) return null;

  if (minute === 0) {
    return hour === 22 ? 23 : hour + 1;
  }

  return hour === 22 ? 23 : hour + 2;
};

// Returns the next available time slot in minutes from midnight
// Always returns the next 15-minute interval, excluding the current one (used by broadcasts)
export const nextAvailableTimeSlot = timezone => {
  const zonedTime = utcToZonedTime(new Date(), timezone);
  const hour = getHours(zonedTime);
  const minute = getMinutes(zonedTime);

  // Always go to the next 15-minute slot (current slot is not selectable)
  const currentMinutes = timeToMinutes(hour, minute);
  const nextSlot = (Math.floor(currentMinutes / 15) + 1) * 15;

  // If we're past the last slot of the day (23:45 = 1425), return null
  if (nextSlot >= 24 * 60) return null;

  return nextSlot;
};

// Hourly time options for current day (used by campaigns)
export const currentDayTimeOptions = (abbrTimzone, timezone) => {
  const nextHour = nextAvailableHour(timezone);

  if (nextHour === null) return [];

  return timeOptions(abbrTimzone).filter(time => {
    return time.value >= nextHour && time.value <= 23;
  });
};

// 15-minute interval time options for current day (used by broadcasts)
export const currentDayTimeOptions15Min = (abbrTimzone, timezone) => {
  const nextSlot = nextAvailableTimeSlot(timezone);

  if (nextSlot === null) return [];

  return timeOptions15Min(abbrTimzone).filter(time => {
    return time.value >= nextSlot;
  });
};
